import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text
} from '@chakra-ui/react';
import React, { FC, useState } from 'react';
import StepBox from 'components/onboarding/deployment/stepBox';
import SelectTokenInput from './selectTokenInput';
import { ProgramToken } from 'types';
import { BonusToken } from '..';
import { useTokenBalance } from 'hooks/useTokenBalance';
import { FormError } from 'components/onboarding/form/FormError';

type Props = {
  tokenList: ProgramToken[];
  bonusTokens: BonusToken[];
  setBonusTokens: React.Dispatch<React.SetStateAction<BonusToken[]>>;
};

const SelectBonusTokens: FC<Props> = ({
  tokenList,
  bonusTokens,
  setBonusTokens
}) => {
  const [selectedToken, setSelectedToken] = useState<ProgramToken>();
  const [amount, setAmount] = useState('');
  const { insufficientBalance, error } = useTokenBalance(selectedToken, amount);

  const onAddBonusToken = () => {
    if (selectedToken) {
      const parsedAmount = amount === '' ? 0.0 : parseFloat(amount);

      // If token already exists, update its amount
      if (
        bonusTokens.find((token) => token.address === selectedToken.address)
      ) {
        const updatedBonusTokens = bonusTokens.map((token) =>
          token.address === selectedToken.address
            ? { ...token, amount: parsedAmount }
            : token
        );
        setBonusTokens(updatedBonusTokens);
      } else {
        const newToken = { ...selectedToken, amount: parsedAmount };
        setBonusTokens([...bonusTokens, newToken]);
      }
    }
  };

  const onRemoveBonusToken = (index: number) => {
    setBonusTokens(bonusTokens.filter((_, i) => i !== index));
  };

  return (
    <StepBox heading='4. Select Bonus Tokens (optional)'>
      <GridItem colSpan={[1, 2, 3, 4]}>
        <Text color='gray.200'>
          These are additional tokens that you will be rewarding users, to a
          maximum of 50 different tokens. The users share of these tokens is
          based on the users share of the main reward token. Adding bonus tokens
          can also be done after the contract has been deployed. Once a token
          address is added it cannot be removed.
        </Text>
      </GridItem>

      <GridItem>
        <SelectTokenInput
          tokenList={tokenList}
          setSelectedToken={setSelectedToken}
        />
      </GridItem>

      <GridItem>
        <FormControl
          isInvalid={insufficientBalance}
          isDisabled={!selectedToken}
        >
          <FormLabel>Enter amount (optional)</FormLabel>
          <Input
            id='amount'
            type='number'
            borderColor={'gray.300'}
            placeholder='0'
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {insufficientBalance && <FormError>{error}</FormError>}
        </FormControl>
      </GridItem>

      <GridItem>
        <Flex height='100%' alignItems='flex-end'>
          <Button
            onClick={onAddBonusToken}
            borderRadius='lg'
            disabled={insufficientBalance}
          >
            Add
          </Button>
        </Flex>
      </GridItem>

      {bonusTokens.length > 0 &&
        bonusTokens.map((token, index) => (
          <GridItem key={index} colStart={index === 0 ? 1 : 'auto'}>
            <Tag size='lg' width='100%' justifyContent='space-between' py={3}>
              <TagLabel color='white'>
                {token.amount.toString()} {token.tokenSymbol.toUpperCase()}
              </TagLabel>
              <TagCloseButton onClick={() => onRemoveBonusToken(index)} />
            </Tag>
          </GridItem>
        ))}
    </StepBox>
  );
};

export default SelectBonusTokens;
